import { apiClient } from './apiClient';
import { AUTH_ENDPOINTS } from '../constants/apiEndpoints';
import { ENV } from '../config/env.config';
import { getDeviceId } from './firebaseNotification';

export interface ILoginCredentials {
	mobile?: string;
	mobile_number?: string;
	username?: string;
	password: string;
}

export interface ITenant {
	id: number;
	uuid: string;
	name: string;
	slug: string;
	status: string;
}

export interface ICompany {
	id: number;
	uuid: string;
	code: string;
	name: string;
}

export interface IBranch {
	id: number;
	code: string;
	name: string;
}

export interface IRole {
	id: number;
	name: string;
	key: string;
	type: string;
}

export interface IAuthUser {
	id: number;
	uuid: string;
	name: string;
	username: string;
	email: string;
	mobile?: string | null;
	country_code?: string | null;
	countryCode?: string | null;
	profile_image?: string | null;
	profileImage?: string | null;
	profileImageUrl?: string | null;
	status: number;
	lastLoginAt?: string;
	tenant?: ITenant;
	company?: ICompany;
	branch?: IBranch;
	role?: IRole;
	worker_id?: number | null;
	worker?: {
		id: number;
		name: string;
		worker_code?: string | null;
	} | null;
	process_ids?: number[];
}

export interface ILoginResponse {
	success: boolean;
	status: string;
	message: string;
	data: {
		token: string;
		user: IAuthUser;
	};
}

export interface IMeResponse {
	success: boolean;
	status: string;
	message: string;
	data: IAuthUser;
}

export interface IChangePasswordPayload {
	old_password: string;
	new_password: string;
}

export interface IChangePasswordResponse {
	success: boolean;
	status: string;
	message: string;
	data?: any;
}

export interface IForgotPasswordPayload {
	email: string;
}

export interface IForgotPasswordResponse {
	success?: boolean;
	status?: string | number;
	message?: string;
	data?: any;
}

export interface IResetPasswordPayload {
	password: string;
}

export interface IResetPasswordResponse {
	success?: boolean;
	status?: string | number;
	message?: string;
	data?: any;
}

export interface IUpdateProfilePayload {
	name?: string;
	email?: string;
	mobile?: string;
	country_code?: string;
	countryCode?: string;
	username?: string;
}

export interface IUpdateProfileResponse {
	success: boolean;
	status: string;
	message: string;
	data?: IAuthUser;
}

// AUTH SERVICES
export const authService = {
	// LOGIN
	async login(credentials: ILoginCredentials): Promise<ILoginResponse> {
		const mobile =
			credentials.mobile ??
			credentials.mobile_number ??
			credentials.username ??
			'';
		const response = await apiClient<ILoginResponse>(AUTH_ENDPOINTS.LOGIN, {
			method: AUTH_ENDPOINTS.LOGIN.method,
			body: {
				mobile,
				password: credentials.password,
			},
			requiresAuth: false,
		});

		// STORE ONLY TOKEN IN LOCALSTORAGE (NO USER DATA)
		const token = response?.data?.token;
		if (token) {
			localStorage.setItem(ENV.TOKEN_KEY, token);
		}
		// PURGE ANY POTENTIAL USER OBJECTS FROM STORAGE
		localStorage.removeItem('stichmaster_user');
		localStorage.removeItem('user');
		sessionStorage.removeItem('stichmaster_user');
		sessionStorage.removeItem('user');

		return response;
	},

	// GET ME PROFILE
	async getMe(): Promise<IAuthUser> {
		const response = await apiClient<IMeResponse>(AUTH_ENDPOINTS.ME, {
			method: AUTH_ENDPOINTS.ME.method,
			requiresAuth: true,
		});

		return response.data;
	},

	// UPDATE PROFILE
	async updateProfile(payload: IUpdateProfilePayload): Promise<IUpdateProfileResponse> {
		return apiClient<IUpdateProfileResponse>(AUTH_ENDPOINTS.UPDATE_PROFILE, {
			method: AUTH_ENDPOINTS.UPDATE_PROFILE.method,
			body: payload,
			requiresAuth: true,
		});
	},

	// UPLOAD PROFILE IMAGE
	async uploadProfileImage(file: File): Promise<any> {
		const formData = new FormData();
		formData.append('image', file);
		return apiClient(AUTH_ENDPOINTS.UPDATE_PROFILE_IMAGE, {
			method: AUTH_ENDPOINTS.UPDATE_PROFILE_IMAGE.method,
			body: formData,
			requiresAuth: true,
		});
	},

	// DELETE PROFILE IMAGE
	async deleteProfileImage(): Promise<any> {
		const formData = new FormData();
		formData.append('delete_image', 'true');
		return apiClient(AUTH_ENDPOINTS.UPDATE_PROFILE_IMAGE, {
			method: AUTH_ENDPOINTS.UPDATE_PROFILE_IMAGE.method,
			body: formData,
			requiresAuth: true,
		});
	},

	// FORGOT PASSWORD
	async forgotPassword(payload: IForgotPasswordPayload): Promise<IForgotPasswordResponse> {
		return apiClient<IForgotPasswordResponse>(AUTH_ENDPOINTS.FORGOT_PASSWORD, {
			method: AUTH_ENDPOINTS.FORGOT_PASSWORD.method,
			body: payload,
			requiresAuth: false,
		});
	},

	// RESET PASSWORD
	async resetPassword(token: string, payload: IResetPasswordPayload): Promise<IResetPasswordResponse> {
		return apiClient<IResetPasswordResponse>(AUTH_ENDPOINTS.RESET_PASSWORD(token), {
			body: payload,
			requiresAuth: false,
		});
	},

	// CHANGE PASSWORD
	async changePassword(payload: IChangePasswordPayload): Promise<IChangePasswordResponse> {
		return apiClient<IChangePasswordResponse>(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
			method: AUTH_ENDPOINTS.CHANGE_PASSWORD.method,
			body: payload,
			requiresAuth: true,
		});
	},

	// LOGOUT
	async logout(): Promise<void> {
		try {
			const controller = new AbortController();
			const timeout = window.setTimeout(() => controller.abort(), 5000);
			try {
				await apiClient(AUTH_ENDPOINTS.LOGOUT, {
					body: { device_id: getDeviceId() },
					signal: controller.signal,
				});
			} finally {
				window.clearTimeout(timeout);
			}
		} catch {
			// Always clear the local session even if the API is unavailable.
		} finally {
			try {
				// eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
				const { socketService } = require('./socketService');
				socketService?.disconnect();
			} catch {
				// Socket cleanup must not block logout.
			}
			localStorage.removeItem(ENV.TOKEN_KEY);
			localStorage.removeItem('stichmaster_user');
			localStorage.removeItem('user');
			sessionStorage.removeItem(ENV.TOKEN_KEY);
			sessionStorage.removeItem('stichmaster_user');
			sessionStorage.removeItem('user');
		}
	},

	// GET TOKEN
	getToken(): string | null {
		return localStorage.getItem(ENV.TOKEN_KEY) || sessionStorage.getItem(ENV.TOKEN_KEY);
	},

	// CHECK FOR THE AUTH
	isAuthenticated(): boolean {
		return !!this.getToken();
	},
};

export default authService;
